import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { numberLocale, useLocale, useT, type Translations } from '@/shared/i18n'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'

const UTILITY = 'cron-generator'
const DEFAULT_EXPRESSION = '*/15 9-18 * * 1-5'
const NEXT_RUN_LIMIT = 5
const MAX_SCAN_MINUTES = 60 * 24 * 366 * 5

type CronFieldKey = 'minute' | 'hour' | 'day' | 'month' | 'weekday'
type FieldMode = 'any' | 'specific' | 'every' | 'range'

interface FieldSpec {
  key: CronFieldKey
  min: number
  max: number
  aliases?: Record<string, number>
}

interface ParsedField {
  values: Set<number>
  unrestricted: boolean
  label: string
}

interface ParsedCron {
  expression: string
  fields: Record<CronFieldKey, ParsedField>
}

type CronResult =
  | {
      ok: true
      parsed: ParsedCron
      nextRuns: Date[]
    }
  | {
      ok: false
      error: string
      field?: CronFieldKey
    }

interface BuilderField {
  mode: FieldMode
  value: string
  step: string
  start: string
  end: string
}

type BuilderState = Record<CronFieldKey, BuilderField>

const MONTH_ALIASES: Record<string, number> = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
}

const WEEKDAY_ALIASES: Record<string, number> = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
}

const FIELD_SPECS: Record<CronFieldKey, FieldSpec> = {
  minute: { key: 'minute', min: 0, max: 59 },
  hour: { key: 'hour', min: 0, max: 23 },
  day: { key: 'day', min: 1, max: 31 },
  month: { key: 'month', min: 1, max: 12, aliases: MONTH_ALIASES },
  weekday: { key: 'weekday', min: 0, max: 7, aliases: WEEKDAY_ALIASES },
}

const FIELD_ORDER: CronFieldKey[] = ['minute', 'hour', 'day', 'month', 'weekday']

const DEFAULT_BUILDER: BuilderState = {
  minute: { mode: 'every', value: '0', step: '15', start: '0', end: '59' },
  hour: { mode: 'range', value: '9', step: '1', start: '9', end: '18' },
  day: { mode: 'any', value: '1', step: '1', start: '1', end: '31' },
  month: { mode: 'any', value: '1', step: '1', start: '1', end: '12' },
  weekday: { mode: 'range', value: '1', step: '1', start: '1', end: '5' },
}

function normalizeWeekday(value: number): number {
  return value === 7 ? 0 : value
}

function fieldError(t: Translations, field: CronFieldKey, message: string): string {
  return `${t.cronGenerator.fields[field]}: ${message}`
}

function parseValue(rawValue: string, spec: FieldSpec, t: Translations): number {
  const normalized = rawValue.trim().toLowerCase()
  const aliasValue = spec.aliases?.[normalized]
  const value = aliasValue ?? Number(normalized)

  if (!Number.isInteger(value)) {
    throw new Error(t.cronGenerator.errors.invalidValue.replace('{value}', rawValue))
  }

  if (value < spec.min || value > spec.max) {
    throw new Error(
      t.cronGenerator.errors.outOfRange
        .replace('{value}', rawValue)
        .replace('{min}', String(spec.min))
        .replace('{max}', String(spec.max)),
    )
  }

  return spec.key === 'weekday' ? normalizeWeekday(value) : value
}

function expandPart(part: string, spec: FieldSpec, t: Translations): number[] {
  const [rangeToken, stepToken] = part.split('/')
  if (!rangeToken) {
    throw new Error(t.cronGenerator.errors.emptyPart)
  }

  const step = stepToken ? Number(stepToken) : 1
  if (!Number.isInteger(step) || step < 1) {
    throw new Error(t.cronGenerator.errors.invalidStep.replace('{step}', stepToken ?? ''))
  }

  let start: number
  let end: number
  if (rangeToken === '*') {
    start = spec.min
    end = spec.max
  } else if (rangeToken.includes('-')) {
    const [startToken, endToken] = rangeToken.split('-')
    if (!startToken || !endToken) {
      throw new Error(t.cronGenerator.errors.invalidRange.replace('{range}', rangeToken))
    }
    start = parseValue(startToken, spec, t)
    end = parseValue(endToken, spec, t)
  } else {
    start = parseValue(rangeToken, spec, t)
    end = start
  }

  if (start > end && spec.key !== 'weekday') {
    throw new Error(t.cronGenerator.errors.invalidRange.replace('{range}', rangeToken))
  }

  if (start > end && spec.key === 'weekday') {
    throw new Error(t.cronGenerator.errors.invalidRange.replace('{range}', rangeToken))
  }

  const result: number[] = []
  for (let value = start; value <= end; value += step) {
    result.push(spec.key === 'weekday' ? normalizeWeekday(value) : value)
  }
  return result
}

function formatValues(values: number[], spec: FieldSpec, t: Translations): string {
  const sorted = [...values].sort((a, b) => a - b)
  if (spec.key === 'weekday') {
    return sorted.map((value) => t.cronGenerator.weekdays[value] ?? String(value)).join(', ')
  }
  if (spec.key === 'month') {
    return sorted.map((value) => t.cronGenerator.months[value - 1] ?? String(value)).join(', ')
  }
  return sorted.map(String).join(', ')
}

function summarizeField(token: string, values: number[], spec: FieldSpec, t: Translations): string {
  const fieldName = t.cronGenerator.fields[spec.key]
  if (token === '*') {
    return t.cronGenerator.summary.any.replace('{field}', fieldName)
  }
  if (token.startsWith('*/')) {
    return t.cronGenerator.summary.every
      .replace('{step}', token.slice(2))
      .replace('{field}', fieldName)
  }
  if (token.includes('-') && !token.includes(',')) {
    return t.cronGenerator.summary.range
      .replace('{field}', fieldName)
      .replace('{values}', formatValues(values, spec, t))
  }
  return t.cronGenerator.summary.specific
    .replace('{field}', fieldName)
    .replace('{values}', formatValues(values, spec, t))
}

function parseField(token: string, spec: FieldSpec, t: Translations): ParsedField {
  if (!token.trim()) {
    throw new Error(t.cronGenerator.errors.emptyField)
  }

  const values = new Set<number>()
  const parts = token.split(',')
  parts.forEach((part) => {
    expandPart(part.trim(), spec, t).forEach((value) => values.add(value))
  })

  if (values.size === 0) {
    throw new Error(t.cronGenerator.errors.emptyField)
  }

  const allValues = Array.from(values)
  return {
    values,
    unrestricted: token === '*',
    label: summarizeField(token, allValues, spec, t),
  }
}

function parseCronExpression(expression: string, t: Translations): ParsedCron {
  const tokens = expression.trim().split(/\s+/)
  if (tokens.length !== 5) {
    throw new Error(t.cronGenerator.errors.fieldCount.replace('{count}', String(tokens.length)))
  }

  const entries = FIELD_ORDER.map((key, index) => {
    const token = tokens[index]
    if (!token) {
      throw new Error(t.cronGenerator.errors.emptyField)
    }
    try {
      return [key, parseField(token, FIELD_SPECS[key], t)] as const
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      throw new Error(fieldError(t, key, message))
    }
  })

  return {
    expression: tokens.join(' '),
    fields: Object.fromEntries(entries) as Record<CronFieldKey, ParsedField>,
  }
}

function matchesDay(parsed: ParsedCron, date: Date): boolean {
  const dayField = parsed.fields.day
  const weekdayField = parsed.fields.weekday
  const dayMatches = dayField.values.has(date.getDate())
  const weekdayMatches = weekdayField.values.has(date.getDay())

  if (dayField.unrestricted && weekdayField.unrestricted) return true
  if (dayField.unrestricted) return weekdayMatches
  if (weekdayField.unrestricted) return dayMatches
  return dayMatches || weekdayMatches
}

function matchesCron(parsed: ParsedCron, date: Date): boolean {
  return (
    parsed.fields.minute.values.has(date.getMinutes()) &&
    parsed.fields.hour.values.has(date.getHours()) &&
    parsed.fields.month.values.has(date.getMonth() + 1) &&
    matchesDay(parsed, date)
  )
}

function findNextRuns(parsed: ParsedCron): Date[] {
  const runs: Date[] = []
  const cursor = new Date()
  cursor.setSeconds(0, 0)
  cursor.setMinutes(cursor.getMinutes() + 1)

  for (let scanned = 0; scanned < MAX_SCAN_MINUTES && runs.length < NEXT_RUN_LIMIT; scanned += 1) {
    if (matchesCron(parsed, cursor)) {
      runs.push(new Date(cursor))
    }
    cursor.setMinutes(cursor.getMinutes() + 1)
  }

  return runs
}

function evaluateCron(expression: string, t: Translations): CronResult {
  try {
    const parsed = parseCronExpression(expression, t)
    const nextRuns = findNextRuns(parsed)
    if (nextRuns.length === 0) {
      return { ok: false, error: t.cronGenerator.errors.noRuns }
    }
    return { ok: true, parsed, nextRuns }
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : String(error) }
  }
}

function formatBuilderField(field: BuilderField): string {
  switch (field.mode) {
    case 'any':
      return '*'
    case 'specific':
      return field.value.trim() || '*'
    case 'every':
      return `*/${field.step.trim() || '1'}`
    case 'range':
      return `${field.start.trim() || '0'}-${field.end.trim() || '0'}`
  }
}

function buildExpression(builder: BuilderState): string {
  return FIELD_ORDER.map((key) => formatBuilderField(builder[key])).join(' ')
}

function formatDateTime(date: Date, localeCode: string): string {
  return new Intl.DateTimeFormat(localeCode, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(date)
}

function FieldBuilder({
  fieldKey,
  value,
  onChange,
}: {
  fieldKey: CronFieldKey
  value: BuilderField
  onChange: (fieldKey: CronFieldKey, value: BuilderField) => void
}) {
  const t = useT()

  const update = (patch: Partial<BuilderField>) => {
    onChange(fieldKey, { ...value, ...patch })
  }

  return (
    <div className="rounded-lg border bg-background p-3">
      <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
        {t.cronGenerator.fields[fieldKey]}
      </label>
      <select
        value={value.mode}
        onChange={(event) => update({ mode: event.target.value as FieldMode })}
        className={cn(
          'h-10 w-full rounded-lg border bg-card px-3 text-sm',
          'focus:outline-none focus:ring-2 focus:ring-primary/35',
        )}
      >
        <option value="any">{t.cronGenerator.builderModes.any}</option>
        <option value="specific">{t.cronGenerator.builderModes.specific}</option>
        <option value="every">{t.cronGenerator.builderModes.every}</option>
        <option value="range">{t.cronGenerator.builderModes.range}</option>
      </select>

      {value.mode === 'specific' && (
        <input
          value={value.value}
          onChange={(event) => update({ value: event.target.value })}
          className={cn(
            'mt-2 h-10 w-full rounded-lg border bg-card px-3 font-mono text-sm',
            'focus:outline-none focus:ring-2 focus:ring-primary/35',
          )}
          placeholder={t.cronGenerator.builderPlaceholders[fieldKey]}
          spellCheck={false}
        />
      )}

      {value.mode === 'every' && (
        <input
          value={value.step}
          onChange={(event) => update({ step: event.target.value })}
          className={cn(
            'mt-2 h-10 w-full rounded-lg border bg-card px-3 font-mono text-sm',
            'focus:outline-none focus:ring-2 focus:ring-primary/35',
          )}
          placeholder={t.cronGenerator.stepPlaceholder}
          inputMode="numeric"
        />
      )}

      {value.mode === 'range' && (
        <div className="mt-2 grid grid-cols-2 gap-2">
          <input
            value={value.start}
            onChange={(event) => update({ start: event.target.value })}
            className={cn(
              'h-10 w-full rounded-lg border bg-card px-3 font-mono text-sm',
              'focus:outline-none focus:ring-2 focus:ring-primary/35',
            )}
            placeholder={t.cronGenerator.rangeStart}
            spellCheck={false}
          />
          <input
            value={value.end}
            onChange={(event) => update({ end: event.target.value })}
            className={cn(
              'h-10 w-full rounded-lg border bg-card px-3 font-mono text-sm',
              'focus:outline-none focus:ring-2 focus:ring-primary/35',
            )}
            placeholder={t.cronGenerator.rangeEnd}
            spellCheck={false}
          />
        </div>
      )}
    </div>
  )
}

export function CronGeneratorPage() {
  const t = useT()
  const { locale } = useLocale()
  const localeCode = numberLocale(locale)
  const [expression, setExpression] = useState(DEFAULT_EXPRESSION)
  const [builder, setBuilder] = useState<BuilderState>(DEFAULT_BUILDER)
  const [result, setResult] = useState<CronResult | null>(() => evaluateCron(DEFAULT_EXPRESSION, t))
  const [copied, setCopied] = useState(false)
  const sourceRef = useRef<'manual' | 'builder'>('manual')
  const initialRenderRef = useRef(true)

  const builderExpression = useMemo(() => buildExpression(builder), [builder])

  const handleEvaluate = useCallback((nextExpression = expression) => {
    const start = performance.now()
    const normalized = nextExpression.trim()
    const source = sourceRef.current
    toolInvoke(UTILITY, { source, expression_length: normalized.length })
    const nextResult = evaluateCron(normalized, t)
    setResult(nextResult)

    if (nextResult.ok) {
      toolSuccess(UTILITY, Math.round(performance.now() - start), {
        source,
        next_runs: nextResult.nextRuns.length,
      })
    } else {
      toolError(UTILITY, 'invalid-expression', {
        source,
        expression_length: normalized.length,
      })
    }
  }, [expression, t])

  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false
      return
    }

    const timeout = window.setTimeout(() => handleEvaluate(), 200)
    return () => window.clearTimeout(timeout)
  }, [handleEvaluate])

  const handleBuilderChange = useCallback((fieldKey: CronFieldKey, value: BuilderField) => {
    const nextBuilder = { ...builder, [fieldKey]: value }
    sourceRef.current = 'builder'
    setBuilder(nextBuilder)
    setExpression(buildExpression(nextBuilder))
    setResult(null)
  }, [builder])

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(expression)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopied(false)
    }
  }, [expression])

  const summaries = result?.ok ? FIELD_ORDER.map((key) => result.parsed.fields[key].label) : []

  return (
    <ToolPageShell toolKey="cronGenerator">
      <section className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-6">
          <Card className="overflow-hidden shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-muted/45 px-4 py-3">
              <div className="text-sm font-medium tracking-normal">
                {t.cronGenerator.expressionTitle}
              </div>
              <Button variant="outline" size="sm" onClick={handleCopy} disabled={!expression}>
                {copied ? t.cronGenerator.copied : t.cronGenerator.copy}
              </Button>
            </div>
            <div className="p-4">
              <label className="block">
                <span className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  {t.cronGenerator.expressionLabel}
                </span>
                <input
                  value={expression}
                  onChange={(event) => {
                    sourceRef.current = 'manual'
                    setExpression(event.target.value)
                    setResult(null)
                  }}
                  placeholder={DEFAULT_EXPRESSION}
                  className={cn(
                    'h-12 w-full rounded-lg border bg-card px-3 font-mono text-base',
                    'focus:outline-none focus:ring-2 focus:ring-primary/35',
                  )}
                  spellCheck={false}
                />
              </label>

              {result && !result.ok && (
                <div className="mt-3 rounded-lg border border-destructive/35 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {result.error}
                </div>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setExpression(DEFAULT_EXPRESSION)
                    sourceRef.current = 'manual'
                    setResult(evaluateCron(DEFAULT_EXPRESSION, t))
                  }}
                >
                  {t.cronGenerator.reset}
                </Button>
              </div>
            </div>
          </Card>

          <Card className="overflow-hidden shadow-sm">
            <div className="border-b bg-muted/45 px-4 py-3 text-sm font-medium tracking-normal">
              {t.cronGenerator.builderTitle}
            </div>
            <div className="grid gap-3 p-4 sm:grid-cols-2 xl:grid-cols-5">
              {FIELD_ORDER.map((fieldKey) => (
                <FieldBuilder
                  key={fieldKey}
                  fieldKey={fieldKey}
                  value={builder[fieldKey]}
                  onChange={handleBuilderChange}
                />
              ))}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3">
              <code className="rounded-md border bg-background px-2 py-1 font-mono text-sm">
                {builderExpression}
              </code>
            </div>
          </Card>
        </div>

        <aside className="space-y-6">
          <Card className="overflow-hidden shadow-sm">
            <div className="border-b bg-muted/45 px-4 py-3 text-sm font-medium tracking-normal">
              {t.cronGenerator.humanTitle}
            </div>
            <CardContent className="p-4">
              {result?.ok ? (
                <div className="space-y-2">
                  {summaries.map((summary) => (
                    <div key={summary} className="rounded-lg border bg-background px-3 py-2 text-sm">
                      {summary}
                    </div>
                  ))}
                  <p className="pt-2 text-xs leading-5 text-muted-foreground">
                    {t.cronGenerator.daySemantics}
                  </p>
                </div>
              ) : (
                <p className="text-sm leading-6 text-muted-foreground">
                  {t.cronGenerator.fixExpression}
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="overflow-hidden shadow-sm">
            <div className="border-b bg-muted/45 px-4 py-3 text-sm font-medium tracking-normal">
              {t.cronGenerator.nextRunsTitle}
            </div>
            <CardContent className="p-4">
              {result?.ok ? (
                <>
                  <ol className="space-y-2">
                    {result.nextRuns.map((date) => (
                      <li
                        key={date.toISOString()}
                        className="rounded-lg border bg-background px-3 py-2 font-mono text-sm"
                      >
                        {formatDateTime(date, localeCode)}
                      </li>
                    ))}
                  </ol>
                  <p className="mt-3 text-xs leading-5 text-muted-foreground">
                    {t.cronGenerator.timezoneCaveat}
                  </p>
                </>
              ) : (
                <p className="text-sm leading-6 text-muted-foreground">
                  {t.cronGenerator.noRuns}
                </p>
              )}
            </CardContent>
          </Card>
        </aside>
      </section>
    </ToolPageShell>
  )
}
